import { CommonModule } from "@angular/common";
import {
  Component,
  ComponentFactoryResolver,
  HostListener,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { MatListModule } from "@angular/material/list";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, takeUntil } from "rxjs";
import { IBreadcrumb, IPageAction } from "../../core/models/common";
import { DataStore } from "../../core/models/data-store";
import { MenuItem } from "../../core/models/menu-item";
import { Project } from "../../core/models/project-field";
import { BreadcrumbService } from "../../core/services/breadcrumb.service";
import { RestService } from "../../core/services/rest.service";
import { SideNavService } from "../../core/services/side-nav/side-nav.service";
import { MaterialModule } from "../../core/shared/material.module";
import { Layouts } from "../../layouts";
import { BaseLayoutComponent } from "../../layouts/base-layout.component";
import { BreadcrumbComponent } from "../breadcrumb/breadcrumb.component";
import { CustomHeaderComponent } from "../common/custom-header/custom-header.component";
import { CautionAlertModalComponent } from "../modal-templates/caution-alert-modal/caution-alert-modal.component";
import { SharedComponents } from "../shared.module";

/**
 * The `SideNavContentComponent` is responsible for rendering and managing the content inside the side navigation bar.
 * It listens for changes to the selected menu item, loads the appropriate component dynamically based on the selected view,
 * and updates the breadcrumb navigation accordingly. It also manages the visibility of the script editor header.
 * This component is used as part of a layout system, dynamically displaying content and interacting with other services
 * such as breadcrumb management and script editor controls.
 */
@Component({
  selector: "app-side-nav-content",
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatListModule,
    BreadcrumbComponent,
    CustomHeaderComponent,
    ...SharedComponents,
  ],
  templateUrl: "./side-nav-content.component.html",
  styleUrl: "./side-nav-content.component.scss",
})
export class SideNavContentComponent implements OnChanges {
  @Input() isSidenavOpen: boolean = true;
  @Input()
  selectedItem!: MenuItem;
  /** Parent nav item — when set, breadcrumb shows "Parent | Child" */
  @Input() parentItem: MenuItem | null = null;

  @ViewChild("appGenericView", { read: ViewContainerRef })
  appGenericView!: ViewContainerRef;

  dataStore: DataStore = {};
  projectDetails: Project = {
    id: "",
    name: "",
  };

  breadcrumb: IBreadcrumb[] = [];
  displayEditorHeader = false;
  private destroy$ = new Subject<void>();
  private modalService = inject(NgbModal);
  isBreadcrumbHidden = false;
  pageActions: IPageAction[] = [];

  /**
   * Constructs the `SideNavContentComponent` and subscribes to necessary observables for routing, breadcrumb updates,
   * and script editor control visibility changes.
   * @param componentFactoryResolver - Used to dynamically resolve and load components.
   * @param route - Activated route to capture route parameters like project ID and name.
   * @param breadcrumbService - Service to manage breadcrumb navigation.
   * @param router - Router service for handling navigation within the application.
   * @param scriptEditorControlsService - Service to manage script editor control visibility.
   * @param sideNav - Service that handles side navigation interactions.
   */
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private route: ActivatedRoute,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly restService: RestService,
    private readonly router: Router,
    private sideNav: SideNavService
  ) {
    this.route.paramMap.subscribe((params) => {
      this.projectDetails.id = params.get("id") as string;
      this.projectDetails.name = params.get("name") as string;
    });

    this.breadcrumbService.getBreadcrumb()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IBreadcrumb[]) => {
        if (data) {
          this.breadcrumb = data;
        }
      });

    this.breadcrumbService.getPageActions$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(actions => {
        this.pageActions = actions;
      });

    // COMMENTED FOR FUTURE REQUIREMENT
    // this.contextMenuService.listenContextMenuChanges().subscribe((data: any) => {
    //   this.selectedItem = {
    //     name: data.item.name,
    //     id: '',
    //     displayName: data.item.displayName,
    //     view: data.type
    //   };
    //   this.loadComponent();
    // });
  }

  /**
   * Lifecycle hook that is triggered when input properties change. If the `selectedItem` changes,
   * it loads the corresponding component and updates the breadcrumb navigation.
   * @param changes - The changes to the input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedItem"] && !changes["selectedItem"].isFirstChange()) {
      if (this.selectedItem) {
        this.loadComponent();
      } else {
        // selectedItem was cleared (project switch): clear the view container so
        // the content area shows blank while the new project's items load.
        this.appGenericView?.clear();
        this.breadcrumbService.clear();
      }
    }
  }

  /**
   * Loads the component based on the selected menu item and updates the breadcrumb navigation.
   * It clears any existing components and dynamically creates the new component for the selected view.
   * @memberof SideNavContentComponent
   */
  loadComponent() {
    this.sideNav.highlightNode(this.selectedItem.displayName);
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(
        this.getComponent(
          this.selectedItem?.view ? this.selectedItem?.view : null
        )
      );
    this.appGenericView.clear();

    this.breadcrumbService.clear();

    if (this.parentItem) {
      this.breadcrumbService.addNode({
        name: this.parentItem.displayName,
        key: this.parentItem.displayName,
        isRootNode: true,
        clickable: false,
      });
    }

    this.breadcrumbService.addNode({
      name: this.selectedItem.displayName,
      key: this.selectedItem.displayName,
      isRootNode: !this.parentItem,
      clickable: false,
    });

    this.getConfiguredLLMs();
    if(this.selectedItem?.view === 'OverviewAgenticAIView' || this.selectedItem?.view === 'WorkspaceAgenticAIView') {
      this.isBreadcrumbHidden = true;
    } else {
      this.isBreadcrumbHidden = false;
    }
    const componentRef = this.appGenericView.createComponent(componentFactory);
    this.dataStore.data = this.projectDetails;
    this.dataStore.activeNode = this.selectedItem;
    (componentRef.instance as BaseLayoutComponent).dataStore = this.dataStore;
  }


  //getGlobally configured llms
  getConfiguredLLMs(){
    this.restService.getLLMConfs(this.projectDetails.id).subscribe((response: any) => {
     
      if (response?.data?.isCommonLLM) {
       console.log("LLM configured globally!")
      } else if(response?.data?.applicationLLMs){
        const configuredLLms = response?.data?.applicationLLMs;
        const key = this.selectedItem?.name;

        if (key in configuredLLms) {
          //asset mapped true, checking llm configuration in mapped assets
          if(configuredLLms[key]?.llmId === null || configuredLLms[key]?.llmId === undefined){
            this.openCautionModal();
          }
        } else if(key === "impactedTestCases" || key === "testCaseCodeMappings"){
          if(configuredLLms['testPrioritizationAndSelection']?.llmId === null || configuredLLms['testPrioritizationAndSelection']?.llmId === undefined){
            this.openCautionModal();
          }
        }
      } else{
        console.log("Warning: No LLMs configured!")

      }
    }, error => {
      console.log("Error occurred while getting the configurred llms!");
    });
  }

  openCautionModal() {
    let modalRef = this.modalService.open(CautionAlertModalComponent, {
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
    title: "Incomplete Setup",
    subTitle: "Please configure the LLM connection before proceeding with asset actions."
    };
    modalRef.result.then((result: any) => {});
  }

  /**
   * Returns the corresponding component for the selected view.
   * If the view is not found, it defaults to the `DefaultLayoutView`.
   * @param view - The view identifier.
   * @returns {Component} - The component to be loaded.
   */
  getComponent(view: any): Type<any> {
    return (view && Layouts[view as keyof typeof Layouts]
      ? Layouts[view as keyof typeof Layouts]
      : Layouts.DefaultLayoutView) as Type<any>;
  }

  /** Relays a header action button click to the active layout component via BreadcrumbService. */
  onActionClick(key: string): void {
    this.breadcrumbService.firePageAction(key);
  }

  @HostListener('document:click')
  closeAllDropdowns(): void {
    this.pageActions.forEach((a: any) => (a['_open'] = false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
